"use client";

import React, { useEffect, useState, useRef } from "react";
import { useSession } from "next-auth/react";
import ScoreModalButton from "@/components/ScoreModalButton";
import { useAppContext } from "@/context/AppContextProvider";
import { bizUDPGothicBold } from "@/lib/utils/fonts";
import { ImageResponseData } from "@/restapi/image";
import { ScoreData } from "@/restapi/score";
import ImageIconComponent from "@/components/icons/ImageIconComponent";
import { useGroupGetWithImage } from "@/hooks/group/useGroupGetWithImage";
import { GroupImageDeleteButton } from "@/components/group/buttons/GroupImageDeleteButton";
import { usePathname } from "next/navigation";
import { URLConstants } from "@/constants/URLConstants";
import { useImageScoreExists } from "@/hooks/image/useImageScoreExists";
import LocateIcon from "@/components/icons/LocateIcon";
import { LuSmilePlus } from "react-icons/lu";
import { IoMdLock } from "react-icons/io";
import { getImageSelectionFromStorage } from "@/lib/utils";
import "./style.css";
import { GroupTypeValue } from "@/restapi/group";
import { useTranslation } from "@/lib/react/i18n";

const ImageScoreCard: React.FC<{
  image: ImageResponseData;
  isActive: boolean;
  audioEnabled: boolean;
}> = ({ image, isActive, audioEnabled }) => {
  const { data: session, status } = useSession();
  const user = session?.user;
  const { t } = useTranslation();

  const [scoreExists, setScoreExists] = useState(
    image.joinContest ? false : image.scoreExists
  );
  const pathname = usePathname();
  const isGroupMaypage = /^\/group\/mypage\/[^/]+$/.test(pathname ?? "");
  const imageRef = useRef<HTMLDivElement>(null);
  const fileUrl = image.imageFile.fileUrl;
  const fileName = image.imageFile.fileName;
  const isVideo = fileName?.toLowerCase().endsWith(".mp4");
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const { visableMode, setVisableMode, settingMenuProps, setSettingMenuProps } =
    useAppContext();

  const [isScorolled, setIsScrolled] = useState(false);
  const [scoreData, setScoreData] = useState<ScoreData>({
    posts: image.posts ?? 0,
    averageScore: image.averageScore,
    totalScore: image.totalScore ?? 0,
  });

  const [isAdmin, setIsAdmin] = useState(false);
  const { data: group, isLoading } = useGroupGetWithImage({
    imageId: image.id,
    userId: user?.id ?? "",
  });

  const { data: scoreExistsData, isLoading: scoreExistsLoading } =
    useImageScoreExists({
      userUuid: user?.id ?? "",
      imageUuid: image.uuid,
    });

  const forcePlay = (v: HTMLVideoElement) => {
    try {
      const p = v.play();
      if (p && typeof p.then === "function") {
        p.catch(() => {});
      }
    } catch {}
  };

  useEffect(() => {
    if (!isVideo) return;
    const v = videoRef.current;
    if (!v) return;

    v.muted = false;
    v.autoplay = true;
  }, [isVideo]);

  useEffect(() => {
    if (group && group.adminIds) {
      setIsAdmin(group.adminIds.includes(user?.id ?? ""));
    }
  }, [group, isLoading, user?.id]);

  useEffect(() => {
    if (!isVideo) return;
    const v = videoRef.current;
    if (!v) return;

    if (isActive) {
      v.play().catch(() => {
        v.volume = audioEnabled ? 1.0 : 0.0;
        v.muted = false;
        v.autoplay = true;
        forcePlay(v);
      });
      v.muted = false;
      v.autoplay = true;
      v.controls = true;
      forcePlay(v);
    } else {
      v.muted = true;
      v.autoplay = true;
      v.controls = true;
      forcePlay(v);
    }
  }, [isActive, audioEnabled, isVideo]);

  useEffect(() => {
    // Backend で anonymous_scorer_id が実装されるまでは、
    // localStorage 判定とAPI判定の両方を使う（フォールバック）
    const imageData = getImageSelectionFromStorage();
    const imageUUid = imageData
      ? Object.keys(imageData).find((uuid) => uuid === image.uuid)
      : undefined;

    // Session がloading 中の場合は処理をスキップ
    if (status === 'loading') {
      return;
    }

    // scoreExistsLoading が false になったら必ず状態を更新（scoreExistsData が undefined でも OK）
    if (!scoreExistsLoading) {
      if (status !== 'authenticated' || !user) {
        // 未認証ユーザーの場合
        if (image.joinContest) {
          if (image.isContestClosed) {
            setScoreExists(true);
          } else if (image.isInEntryPeriod) {
            setScoreExists(true);
          } else if (image.isContestOpen) {
            // localStorage に記録があればそれを優先、なければ API 判定を使う
            setScoreExists(!!imageUUid || (scoreExistsData?.scoreExists ?? false));
          }
        } else if (
          image.group === null ||
          image.group.groupType === GroupTypeValue.PUBLIC
        ) {
          // localStorage に記録があればそれを優先、なければ API 判定を使う
          setScoreExists(!!imageUUid || (scoreExistsData?.scoreExists ?? false));
        }
      } else {
        // 認証ユーザーの場合
        setScoreExists(scoreExistsData?.scoreExists ?? false);
      }
    }
  }, [scoreExistsLoading, scoreExistsData, status, image, user]);

  const handleToggleVisibility = () => {
    if (settingMenuProps.isMeneuOpen) {
      setSettingMenuProps({ isMeneuOpen: false });
      return;
    }
    setVisableMode({ isShow: !visableMode.isShow });
  };

  const handleClickGroupMypageLink = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (group && typeof window !== "undefined") {
      window.location.href = `${URLConstants.group.mypage.pathname}/${group.id}`;
    }
  };

  const handleClickUserMypageLink = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();
    if (typeof window !== "undefined") {
      window.location.href = `${URLConstants.user.mypage.pathname}/${image.userProfile.uuid}`;
    }
  };

  useEffect(() => {
    if (imageRef.current) {
      imageRef.current.style.transform = `translateY(${window.scrollY}px)`;
    }
    setIsScrolled(true);
  }, []);

  // Session loading 中、またはグループ・スコア情報の読み込み中は何も表示しない
  if (status === 'loading' || isLoading || scoreExistsLoading) return null;

  return (
    <div
      id={`image-${image.uuid}`}
      data-testid={`image-card-${image.uuid}`}
      ref={imageRef}
      className={`${isScorolled ? "opacity-100" : "opacity-0"} relative w-100 h-screen`}
      onClick={handleToggleVisibility}
    >
      <div className="text-with-outline-shadow average-score">
        {typeof scoreData.averageScore === "number" &&
          scoreData.posts !== null && (
            <div className={`${visableMode.isShow ? "fade-in" : "fade-out"}`}>
              <div
                className={`mt-24 cursor-pointer avgScoreBox drop-shadow-[1px_1px_2px_rgba(0,0,0,0.8)] mix-blend-multiply ${
                  visableMode.isShow ? "fade-in" : "fade-out"
                }`}
              >
                <span 
                  className="avgScore"
                  data-testid={`image-score-average-${image.uuid}`}
                >
                  {scoreData.averageScore}
                </span>
                <span className="avgTxt">avg.</span>
              </div>
              <div className="my-2 border-b-2 border-white rounded-none w-24 drop-shadow-[1px_1px_2px_rgba(0,0,0,0.8)]"></div>
              <div
                className="flex items-center"
                style={{ filter: "drop-shadow(2px 2px 4px rgba(0,0,0,0.6))" }}
              >
                <LuSmilePlus
                  className={`text-w-6 h-6 ${
                    image.posts && image.posts >= 30
                      ? "text-pink-300"
                      : "text-white"
                  }`}
                />
                <span
                  className={`block text-xl font-bold ml-1 ${
                    image.posts && image.posts >= 30
                      ? "text-pink-300"
                      : "text-white"
                  }`}
                  data-testid={`image-score-count-${image.uuid}`}
                >
                  {image.posts}
                </span>
              </div>
            </div>
          )}
      </div>

      {isVideo ? (
        <video
          ref={videoRef}
          className={`absolute inset-0 w-full h-full ${
            visableMode.isShow ? "object-cover" : "object-contain"
          }`}
          src={fileUrl}
          playsInline
          loop
          autoPlay={true}
          muted={!isActive}
        />
      ) : (
        <img
          src={fileUrl}
          alt={fileName}
          loading="eager"
          decoding="async"
          fetchPriority="high"
          className={`absolute inset-0 w-full h-full ${
            visableMode.isShow ? "object-cover" : "object-contain"
          }`}
        />
      )}

      {image.isLocked && (
        <div className="absolute top-24 left-4 z-30 max-w-xs">
          <div
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap bg-orange-500/15 text-orange-200 border border-orange-400/20 ${
              visableMode.isShow ? "fade-in" : "fade-out"
            }`}
            title={image.lockReason ?? t("Locked")}
          >
            <IoMdLock className="w-4 h-4" />
            {t("Locked")}
          </div>

          <div
            className={`mt-2 space-y-1 text-[11px] text-orange-100/70 ${
              visableMode.isShow ? "fade-in" : "fade-out"
            }`}
          >
            <div className="text-orange-200/90">
              {t("Locked")}
            </div>

            {image.lockReason && (
              <div>
                {t("Lock Reason")}:{" "}
                <span className="text-orange-100/90">{image.lockReason}</span>
              </div>
            )}

            {image.lockedBy && (
              <div>
                {t("Locked By")}:{" "}
                <span className="text-orange-100/90 break-all">{image.lockedBy}</span>
              </div>
            )}

            {image.lockedAt && (
              <div>
                {t("Locked At")}:{" "}
                <span className="text-orange-100/90">{image.lockedAt}</span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mb-16">
        {user &&
          (((group && (isAdmin || image.userProfile.uuid === user.id) && isGroupMaypage) ||
            image.userProfile.uuid === user.id) && (
            <div
              className={`absolute top-32 right-4 ${
                visableMode.isShow ? "fade-in" : "fade-out"
              }`}
            >
              <GroupImageDeleteButton image={image} group={group} />
            </div>
          ))}
      </div>

      <div>
        {!scoreExists && (
          <ScoreModalButton
            setScoreExists={setScoreExists}
            imageData={image}
            setScoreData={setScoreData}
          />
        )}
      </div>

      <div className="absolute w-full bottom-0 sm:bottom-[5%] left-0 px-2 pb-28 md:pb-20 grid grid-cols-2 grid-rows-1 gap-4">
        <div className="flex flex-row items-end">
          <div
            className={`opacity-65 score-number ${bizUDPGothicBold.className} ${
              visableMode.isShow ? "fade-in" : "fade-out"
            }`}
          >
            {!image.joinContest && <span className="opacity-65">{image.myScore}</span>}
          </div>
          <div className="flex flex-col pb-2">
            <div className="mapMark">
              <LocateIcon
                className={`text-with-outline-shadow locate-icon ${
                  visableMode.isShow ? "fade-in" : "fade-out"
                }`}
              />
            </div>
            <div className="ml-2">
              <div className="cityBtn">
                <span
                  className={`text-white text-with-outline-shadow city-name ${
                    visableMode.isShow ? "fade-in" : "fade-out"
                  }`}
                >
                  {`${image.province} ${image.city}`}
                </span>
              </div>
              <div className="accountBtn">
                <span
                  className={`text-white text-with-outline-shadow account-name ${
                    visableMode.isShow ? "fade-in" : "fade-out"
                  }`}
                >
                  @{image.userProfile.userId}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-end justify-end">
          {group && group.id !== undefined && (
            <div
              className={`cursor-pointer ${
                visableMode.isShow ? "fade-in" : "fade-out"
              }`}
              onClick={handleClickGroupMypageLink}
            >
              <ImageIconComponent
                fileURL={group?.iconFileUrl ?? ""}
                plusDisabledIcon={true}
                iconType={"Group"}
              />
            </div>
          )}
          <div
            className={`cursor-pointer ml-4 ${
              visableMode.isShow ? "fade-in" : "fade-out"
            }`}
            onClick={handleClickUserMypageLink}
          >
            <ImageIconComponent
              fileURL={image.userProfile?.userIconFile?.fileUrl ?? ""}
              plusDisabledIcon={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageScoreCard;